export interface ChromeVisibility {
  showTopNav: boolean;
  showBottomNav: boolean;
  showSidebar: boolean;
}

/**
 * Full-screen task flows where the BottomNav is intentionally hidden
 * (forms, wizards, full-page tools) plus pages that carry their own fixed
 * bottom action bar (the product-detail buy-bar) which the floating nav would
 * collide with. Everything else KEEPS the bottom nav — including browse pages
 * (dao, shops vendor pages, search, orders) so users can always navigate.
 * Matched as path segments (prefix-aware).
 */
const FOCUS_BOTTOM_NAV_HIDDEN = [
  "/checkout",
  "/fast-checkout/ticket-details",
  "/profile/addresses/create",
  "/profile/addresses/edit",
  "/profile/vendor/seller-verification",
  "/info/investment/calculator",
  "/profile/dashboard/calculate-guide",
  "/feedback",
  // Write-review focus flow (stars + comment form). The hub /profile/comments
  // keeps its nav — only the /write sub-route is focus-mode (prefix match).
  "/profile/comments/write",
  // Product detail has a fixed bottom buy-bar (add-to-cart) — its own bottom
  // chrome — so the floating BottomNav is hidden here. Vendor pages (/shops/<id>)
  // are NOT affected; only /shops/items/<id>.
  "/shops/items",
];

const matches = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export function resolveChrome(
  pathname: string | null | undefined,
): ChromeVisibility {
  const path = pathname ?? "";

  // The landing page carries its own responsive navigation and footer.
  if (path === "/") {
    return { showTopNav: false, showBottomNav: false, showSidebar: false };
  }

  // Fast-checkout per-slot ticket purchase flow (e.g. /fast-checkout/<id>/ticket) is focus-mode,
  // but the fast-checkout landing (/fast-checkout) keeps the nav.
  const isFastCheckoutTicket = /^\/fast-checkout\/[^/]+\/ticket(\/|$)/.test(
    path,
  );

  const showBottomNav =
    !isFastCheckoutTicket &&
    !FOCUS_BOTTOM_NAV_HIDDEN.some((p) => matches(path, p));

  // TopNav hidden only on seller-verification capture sub-steps, EXCEPT the
  // capture screen itself and the status screen (kept from the legacy rule).
  const inSellerVerificationSub =
    path.includes("/seller-verification/") &&
    !path.includes("/capture") &&
    !path.includes("/status");

  // The desktop side-nav hides on exactly the same full-screen focus flows as
  // the BottomNav (it occupies the same "this page wants the full width" intent).
  // `lg`+ gating is handled in CSS by AppProvider, not here.
  const showSidebar = showBottomNav;

  return {
    showTopNav: !inSellerVerificationSub,
    showBottomNav,
    showSidebar,
  };
}
